import React, { useState } from "react";
import styled from "styled-components";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import { Img, Picture } from "@onnit-js/ui/components/image";
import Box from "@onnit-js/ui/components/box/Box";
import { Sup, Text } from "@onnit-js/ui/components/text";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import MediaHero01 from "@onnit-js/ui/components/module/MediaHero01";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import Embed from "@onnit-js/ui/components/video/Embed";
import ImgDualPortrait from "@onnit-js/ui/components/module/ImgDualPortrait";
import MediaCollage02 from "@onnit-js/ui/components/module/MediaCollage02";
import MediaCollage03 from "@onnit-js/ui/components/module/MediaCollage03";
import CopyDisplay02 from "@onnit-js/ui/components/module/CopyDisplay02";
import MediaContentShowcase from "@onnit-js/ui/components/module/MediaContentShowcase";
import Video from "@onnit-js/ui/components/video/Video";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import ingredients from "./ingredients";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import SupplementFacts from "../../components/common/SupplementFacts";
import usePageData from "../../hooks/usePageData";
import config from "../../config/config";

const CustomList = styled.ol`
    list-style: none;
    line-height: 1.25;
    counter-reset: item;
    font-family: ${(prp) => prp.theme.fonts.primary};
    font-size: ${(prp) => prp.theme.fontSizes[2]}px;
    @media ${(prp) => prp.theme.mediaQueries.sm} {
        font-size: ${(prp) => prp.theme.fontSizes[4]}px;
    }

    li {
        display: block;
        color: ${(prp) => prp.theme.colors.grays[5]};
        font-weight: ${(prp) => prp.theme.fontWeights.bold};
        padding-left: 42px;
        position: relative;
        margin-bottom: 1.25em;
    }

    li:before {
        position: absolute;
        left: 0;
        top: 0;
        content: counter(item) ". ";
        counter-increment: item;
        color: black;
        font-style: italic;
        font-weight: ${(prp) => prp.theme.fontWeights.heavy};
    }
`;

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/${pageData.slug}`;
    const VID_URL = `${onnitContext.url.cdn.static}/images/product-page/${pageData.slug}`;
    const { IMG_PARAMS } = config;

    const orange = "#FE8E25";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid variant="layout3">
                        <Img
                            src={`${IMG_URL}/BuyBox-1.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="entire alpha brain black label bottle"
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-2.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="alpha brain black label package lid"
                            width={920}
                            height={920}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/BuyBox-3-mobile.jpg${IMG_PARAMS}`,
                                `${IMG_URL}/BuyBox-3.jpg${IMG_PARAMS}`,
                            ]}
                            alt="alpha brain black label in container up close"
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-4.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="alpha brain black label in container"
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-5.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="alpha brain black label package from top"
                            width={920}
                            height={920}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <CopyDisplay02 pt={[0, 0, 8]} pb={[6, 6, 8]}>
                        <Text as="h2" typeStyle="copyDisplay02">
                            Alpha BRAIN
                            <Sup variant="reg" /> Black Label is the ultimate break-glass-in-case-of-emergency,
                            must-get-stuff-done NOW formula.
                        </Text>
                    </CopyDisplay02>

                    <Box bg="black" py={[6, 8, 10]}>
                        <MediaHero01
                            mb={[6, 8, 10]}
                            media={(
                                <Img
                                    src={`${IMG_URL}/abbl-lifestyle-01.jpg${IMG_PARAMS}`}
                                    alt="Joe Rogan Shooting pool."
                                    width={1168}
                                    height={1168}
                                />
                            )}
                            headline={(
                                <Text as="h2" typeStyle="headlineHero01" color="white" fontSize={[12, 14, 14, 18]}>
                                    Refine <br />
                                    Your <br />
                                    Flow <br />
                                    State
                                </Text>
                            )}
                        />

                        <ImageContentPortrait
                            mb={[6, 6, 8]}
                            reverse
                            image={(
                                <Img
                                    src={`${IMG_URL}/abbl-lifestyle-02.jpg${IMG_PARAMS}`}
                                    alt="Friends gaming."
                                    width={930}
                                    height={1200}
                                />
                            )}
                            content={(
                                <Box>
                                    <Text as="h3" color={orange} typeStyle="copyHero02" mb={4}>
                                        What is flow state?
                                    </Text>
                                    <Text as="p" fontSize={[2, 2, 3]} color="white" lineHeight={6}>
                                        You may have experienced a flow state at some point — that sense of fluidity
                                        between your body and mind, where you are totally absorbed by and deeply focused
                                        on something, beyond the point of distraction. Time feels like it has slowed
                                        down. Your senses are heightened. You are at one with the task at hand, as
                                        action and awareness sync to create an effortless momentum. Some people describe
                                        this feeling as being "in the zone".
                                    </Text>
                                </Box>
                            )}
                        />

                        <TwelveColumn display={["block", "block", "block", "grid"]}>
                            <Box gridColumn="4 / -1" px={4}>
                                <Text as="h2" typeStyle="headlineHero02" color="white" mb={6}>
                                    One Step Closer
                                </Text>

                                <Text
                                    as="p"
                                    fontSize={[2, 2, 2, 3]}
                                    color="white"
                                    lineHeight={6}
                                    mb={[6, 6, 8]}
                                    maxWidth="824px"
                                >
                                    Considered by many to be our most potent Alpha BRAIN
                                    <Sup variant="reg" /> product yet, this formula features new ingredients that
                                    support alertness, focus, and brain and eye health, to give you a serious edge in
                                    whatever you’re doing—on the job, at the wheel, or in front of the computer screen.
                                    <Sup />
                                </Text>
                            </Box>
                            <Box gridColumn={["1 / -1", "1/-1", "4 / span 7"]} px={4}>
                                <Embed
                                    title="Alpha BRAIN Black Label product video"
                                    src="https://player.vimeo.com/video/563785720"
                                />
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <MediaCollage03
                        py={[6, 6, 8]}
                        item1={(
                            <Img
                                src={`${IMG_URL}/MediaCollage03---2.jpg${IMG_PARAMS}`}
                                alt="hands opening alpha brain black label bottle"
                                width={1170}
                                height={660}
                            />
                        )}
                        item2={(
                            <Img
                                src={`${IMG_URL}/MediaCollage03---1.jpg${IMG_PARAMS}`}
                                alt="alpha brain black label bottle"
                                width={450}
                                height={450}
                            />
                        )}
                        item3={(
                            <Img
                                src={`${IMG_URL}/MediaCollage03---3.jpg${IMG_PARAMS}`}
                                alt="zoomed in on text reading razor sharp focus"
                                width={690}
                                height={390}
                            />
                        )}
                    />

                    <Box bg="black" py={[6, 6, 8]}>
                        <TwelveColumn display={["block", "block", "block", "grid"]} px={4} mb={[8, 8, 8, 10]}>
                            <Text
                                as="h2"
                                gridColumn="2 / -2"
                                fontWeight="black"
                                color="white"
                                fontSize={[7, 14, 15]}
                                lineHeight={2}
                                my={[4, 4, 4, 8]}
                            >
                                How Alpha BRAIN
                                <Sup variant="reg" />
                                <Box as="span" display={["inline", "inline", "inline", "block"]} />
                                Black Label Works
                            </Text>

                            <Box
                                bg={["transparent", "grays.7"]}
                                gridColumn={["4 / -2", "4 / -2", "6 / span 5"]}
                                p={[0, 5]}
                            >
                                <Text as="p" fontSize={[2, 2, 3]} lineHeight={6} color="white">
                                    As with the original product, Alpha BRAIN
                                    <Sup variant="reg" /> Black Label helps the brain get in the zone and process
                                    information. It promotes flow state (that feeling of deep focus), and helps maintain
                                    levels of neurotransmitters, chemicals that relay signals to and from the brain.
                                    <Sup />
                                    <br />
                                    <br />
                                    But with the addition of several new ingredients, Alpha BRAIN
                                    <Sup variant="reg" /> Black Label can promote attention span, cognitive performance,
                                    learning, long-term memory, and a state of relaxed alertness that lets you zero in
                                    on tasks without feeling jittery. When taken daily, it can also support brain and
                                    eye health, helping to regulate the stress that comes from long periods of hard work
                                    in front of a screen.
                                    <Sup />
                                </Text>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn display={["block", "block", "block", "grid"]} px={5}>
                            <Text
                                as="h3"
                                typeStyle="copyDisplay02"
                                color="white"
                                gridColumn={["1 / -1", "1 / -1", "2 / span 12"]}
                            >
                                Key Ingredients
                            </Text>
                        </TwelveColumn>

                        <MediaContentShowcase
                            items={ingredients.map((mediaContentShowcaseItem) => {
                                const copy = { ...mediaContentShowcaseItem };
                                copy.image = `${IMG_URL}/${mediaContentShowcaseItem.image}`;
                                return copy;
                            })}
                            textColor="white"
                            activeColor={orange}
                            bgMedia={(
                                <Img
                                    src={`${IMG_URL}/mediaShowcase01.jpg${IMG_PARAMS}`}
                                    alt="abstract background"
                                    width={1120}
                                    height={315}
                                />
                            )}
                        />
                    </Box>

                    <ImageContentPortrait
                        py={[6, 6, 8]}
                        image={(
                            <Img
                                src={`${IMG_URL}/abbl-lifestyle-03.jpg${IMG_PARAMS}`}
                                alt="Joe Rogan holding bottle of Alpha brain black label."
                                width={930}
                                height={1200}
                            />
                        )}
                        content={(
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text
                                    as="h2"
                                    typeStyle="copyDisplay02"
                                    mb={[4, 4, 6]}
                                    style={{ textTransform: "none" }}
                                >
                                    Should I Take Regular Alpha BRAIN
                                    <Sup variant="reg" /> or Alpha BRAIN
                                    <Sup variant="reg" /> Black Label?
                                </Text>
                                <Text as="p" typeStyle="copyHero02">
                                    Think of regular Alpha BRAIN
                                    <Sup variant="reg" /> as a good supplement to take daily for supporting cognitive
                                    function.
                                    <Sup /> But, on days that you’re really under the gun and need to be extra focused
                                    and productive, Alpha BRAIN
                                    <Sup variant="reg" /> Black Label is the better option.
                                    <Sup /> (Note: DO NOT take both products in a 24-hour period.)
                                </Text>
                            </Box>
                        )}
                    />

                    <ImgDualPortrait mb={4}>
                        <Img
                            src={`${IMG_URL}/abbl-lifestyle-04.jpg${IMG_PARAMS}`}
                            alt="Man focusing during meeting."
                            width={930}
                            height={930}
                        />
                        <Img
                            src={`${IMG_URL}/abbl-lifestyle-05.jpg${IMG_PARAMS}`}
                            alt="Man with headphones sitting on ground working on production."
                            width={930}
                            height={1328}
                        />
                    </ImgDualPortrait>

                    <TwelveColumn display={["block", "block", "block", "grid"]}>
                        <Text as="p" typeStyle="copyHero02" mt={[0, 0, 0, 6]} gridColumn="8 / span 4" px={3}>
                            When your boss assigns you a big project with a tight deadline, or you need to cram for a
                            test the next day, think of Black Label as being like a cold shower, meditation, and a slap
                            in the face all rolled into one. That’s why we call Black Label our
                            "break-glass-in-case-of-emergency, must-get-stuff-done-NOW" formula.
                        </Text>
                        <Box gridColumn="3 / span 8" px={3} my={[6, 6, 6, 8]}>
                            <Video autoPlay loop muted playsInline placeholder={[800, 450]} aria-hidden="true">
                                <source
                                    src={`${VID_URL}/ImageLandscape01.mp4`}
                                    type="video/mp4"
                                    title="Alpha BRAIN Black Label package animation"
                                />
                            </Video>
                        </Box>
                    </TwelveColumn>

                    <CopyDisplay02 py={[4, 4, 4, 6]}>
                        <Text as="h2" typeStyle="copyDisplay02" mb={[6, 6, 6, 8]} style={{ textTransform: "none" }}>
                            You may also want to try Black Label if you’ve experimented with regular Alpha BRAIN
                            <Sup variant="reg" /> before and didn’t get the desired effect. As Black Label is a
                            different formula, you may achieve a better result than with our original, daily formula.
                        </Text>

                        <Text as="p" typeStyle="copyHero02" mb={4} maxWidth="740px">
                            Lastly, if you spend a lot of time in front of the computer or on your smartphone, you may
                            choose to take Black Label daily instead of Alpha BRAIN
                            <Sup variant="reg" /> to help maintain eye health.
                            <Sup /> Taken regularly, Black Label can help with the eye strain associated with staring at
                            a screen for long periods.
                            <Sup />
                        </Text>
                    </CopyDisplay02>

                    <MediaCollage02
                        item1={(
                            <Img
                                src={`${IMG_URL}/abbl-lifestyle-06.jpg${IMG_PARAMS}`}
                                alt="Joe Rogan shooting pool."
                                width={460}
                                height={345}
                            />
                        )}
                        item2={(
                            <Img
                                src={`${IMG_URL}/abbl-lifestyle-07.jpg${IMG_PARAMS}`}
                                alt="Man holding alpha brain black label bottle."
                                width={690}
                                height={690}
                            />
                        )}
                        item3={(
                            <Img
                                src={`${IMG_URL}/abbl-lifestyle-08.jpg${IMG_PARAMS}`}
                                alt="Men working out playing tug of war."
                                width={1170}
                                height={880}
                            />
                        )}
                    />

                    <TwelveColumn display={["block", "block", "block", "grid"]} py={[6, 6, 6, 8]} px={4}>
                        <Box gridColumn="3 / span 8">
                            <Text as="h2" typeStyle="headlineHero02" mb={[6, 6, 6, 8]}>
                                To Sum It All Up
                            </Text>
                            <CustomList>
                                <li>
                                    Alpha BRAIN
                                    <Sup variant="reg" /> Black Label augments our original formula with the addition of
                                    ingredients that allow for focused attention, mental acuity, quicker processing and
                                    greater accuracy.
                                    <Sup />
                                </li>

                                <li>
                                    The synergistic combination of low-dose caffeine and L-Theanine offers a sense of
                                    pleasant motivation without nervousness and, instead, instills a feeling of relaxed
                                    alertness.
                                    <Sup />
                                </li>

                                <li>
                                    Finally, the addition of Lutemax’s lutein and zeaxanthin revolutionizes this
                                    nootropic formula, adding the ultimate brain and eye food that offers macular care
                                    and blue light eye protection
                                    <Sup />
                                    —perfect for those subjected to long periods of electronic screen time!
                                    <Sup />
                                </li>
                            </CustomList>
                        </Box>
                    </TwelveColumn>
                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers
                        supplementWarning
                        supplementFacts={<SupplementFacts currentPID={currentPID} />}
                    >
                        <Text as="p" fontSize={0} color="grays.4" my={2}>
                            COGNIZIN
                            <Sup variant="reg" /> is a registered trademark of Kyowa Hakko Bio Co., Ltd
                            <br />
                            SerinAid
                            <Sup variant="reg" /> is a registered trademark of Chemi Nutra.
                        </Text>
                    </UsageAndDisclaimers>
                )}
            </>
        </ThemeProvider>
    );
}
